import { getApiUrl } from '@/lib/api-config';
import { uploadEntityAttachments, fetchEntityAttachments, updateAttachment, deleteAttachment } from '@/lib/entity-attachments';

export interface VendorAttachment {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  file_url: string;
  created_at: string;
  created_by: string;
  created_by_user?: {
    display_name?: string | null;
  };
}

export async function uploadVendorAttachments(
  vendorId: string | undefined,
  files: File[],
  companyId: string,
  userId: string
): Promise<VendorAttachment[]> {
  return uploadEntityAttachments('user', vendorId, files, companyId, userId, { metadata: { document_type: 'vendor_document' } });
}

export function deleteVendorAttachment(
  attachmentId: string,
  userId: string | undefined
): Promise<boolean> {
  return deleteAttachment(attachmentId, userId);
}

export interface VendorDocument {
  id: string;
  name: string;
  file: File | null;
  hasExpiry: boolean;
  expiryDate: string;
  uploadedFileId?: string;
  fileUrl?: string;
  file_path?: string;
  isExisting?: boolean;
}

export async function fetchVendorDocuments(
  vendorId: string,
  companyId: string
): Promise<VendorDocument[]> {
  if (!vendorId || !companyId) return [];
  const rows = await fetchEntityAttachments('user', vendorId, { documentType: 'vendor_document', hasExpiry: true, ascending: false });
  return rows.map((row) => ({
    id: row.id,
    name: row.file_name || '',
    file: null,
    hasExpiry: row.metadata?.has_expiry === true || row.metadata?.has_expiry === 'true',
    expiryDate: (row.metadata?.expiry_date as string) || '',
    uploadedFileId: row.id,
    fileUrl: row.file_url,
    isExisting: true,
  }));
}

export async function uploadVendorDocument(
  vendorId: string | undefined,
  document: VendorDocument,
  companyId: string,
  userId: string
): Promise<string | null> {
  if (!vendorId || !document.file || !document.name.trim()) return null;
  const [created] = await uploadEntityAttachments('user', vendorId, [document.file], companyId, userId, {
    metadata: {
      document_type: 'vendor_document',
      has_expiry: document.hasExpiry,
      expiry_date: document.hasExpiry ? document.expiryDate : undefined,
    },
  });
  if (!created) return null;
  // The user-chosen document name replaces the raw file name.
  if (document.name.trim() !== created.file_name) await updateAttachment(created.id, { file_name: document.name.trim() });
  return created.id;
}

export async function updateVendorDocument(
  documentId: string,
  document: VendorDocument,
  _companyId: string,
  _userId: string
): Promise<boolean> {
  if (!documentId || !document.name.trim()) return false;
  return updateAttachment(documentId, {
    file_name: document.name.trim(),
    metadata: { has_expiry: document.hasExpiry, expiry_date: document.hasExpiry ? document.expiryDate : undefined },
  });
}

export async function deleteVendorDocument(
  documentId: string,
  userId: string | undefined
): Promise<boolean> {
  return deleteVendorAttachment(documentId, userId);
}

export async function fetchVendorAttachments(
  recordId: string,
  companyId: string
): Promise<VendorAttachment[]> {
  if (!recordId || !companyId) return [];
  return fetchEntityAttachments('user', recordId, { documentType: 'vendor_document', hasExpiry: false, ascending: false });
}


export interface NewVendorData {
  first_name?: string;
  last_name?: string;
  email: string;
  phone?: string;
  /** required: a vendor is identified by its company (the contact's name is optional) */
  company_name: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  zip_code?: string;
  notes?: string;
  cost_code_ids?: string[];
  is_active?: boolean;
}

/**
 * The ONE implementation of "create a vendor": the Vendors page and every "Create new" vendor picker (Bids, ...) call it.
 * ONE generic-API call: the server creates the person AND their role in this company ('vendor') together, so a failure leaves no half-created vendor
 * (the company and the author come from the server). Errors carry the server's own message (e.g. a duplicate email) in `error.message`, plus its `details` / `code`.
 */
export async function createVendorUser(
  companyId: string,
  actorUserId: string,
  formData: NewVendorData
): Promise<{ id: string; name: string }> {
  const response = await fetch(getApiUrl('api/entities/user'), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: actorUserId,
      company_id: companyId,
      user_type: 'vendor', // picks the server's vendor rule (company name required, vendors.create); never stored
      first_name: formData.first_name ?? '',
      last_name: formData.last_name ?? '',
      email: formData.email,
      phone: formData.phone,
      company_name: formData.company_name,
      address: formData.address,
      city: formData.city,
      state: formData.state,
      country: formData.country,
      zip_code: formData.zip_code,
      notes: formData.notes,
      cost_code_ids: formData.cost_code_ids ?? [],
      is_active: formData.is_active ?? true,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw Object.assign(new Error(errorData.error || errorData.message || 'Failed to save vendor'), { details: errorData.details, code: errorData.code });
  }
  const newUser = (await response.json()).data;

  const name = formData.company_name || `${formData.first_name ?? ''} ${formData.last_name ?? ''}`.trim() || formData.email;
  return { id: newUser.local_id, name };
}


/**
 * The ONE implementation of "edit a vendor": the person's fields (users) and the vendor's active flag (their role in this company).
 * The Vendors page and the vendor editor opened from an Item both call it. `error.message` carries the server's message, plus its
 * `details` / `code`.
 */
export async function updateVendorUser(vendorId: string, fields: Partial<NewVendorData> & Record<string, any>): Promise<void> {
  const { is_active, ...userData } = fields;
  const fail = async (response: Response, fallback: string) => {
    const errorData = await response.json().catch(() => ({}));
    throw Object.assign(new Error(errorData.error || errorData.message || fallback), { details: errorData.details, code: errorData.code });
  };
  const userResponse = await fetch(getApiUrl(`api/entities/user/${vendorId}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(userData),
  });
  if (!userResponse.ok) await fail(userResponse, 'Failed to save vendor');

  // The active flag lives on the role (PUT /api/user-role/:userId -> PeopleService.changeRole, which requires `role` even when only is_active changes).
  if (is_active !== undefined) {
    const roleResponse = await fetch(getApiUrl(`api/user-role/${vendorId}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role: 'vendor', is_active }),
    });
    if (!roleResponse.ok) await fail(roleResponse, 'Failed to update vendor relationship');
  }
}
